import { useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import type { MatchDto } from "@/types/api";
import { useMatchActions } from "@/app/useMatchActions";
import { MatchCard } from "@/components/MatchCard";
import { OtherTipsModal } from "@/components/OtherTipsModal";
import { TeamStatsModal, type TeamRef } from "@/components/TeamStatsModal";
import { Tabs } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { dayKey, formatDayHeading } from "@/lib/stage";

interface MatchDay {
  key: string;
  date: string;
  matches: MatchDto[];
}
interface Week {
  key: string;
  days: MatchDay[];
}
type Mode = "day" | "week";

/** A dátumot tartalmazó hét hétfőjének kulcsa (yyyy-mm-dd). */
function weekKey(iso: string): string {
  const d = new Date(iso);
  const dow = (d.getDay() + 6) % 7; // hétfő = 0
  d.setDate(d.getDate() - dow);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("hu-HU", { month: "short", day: "numeric" });
}

/** Meccsnaponként vagy -hetenként csoportosított tippelő-felület (csoportkör és kieséses egyaránt). */
export function MatchDayBoard({ matches, showStage }: { matches: MatchDto[]; showStage?: boolean }) {
  const { submitTip, toggleTuti } = useMatchActions();
  const [selectedMatch, setSelectedMatch] = useState<MatchDto | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamRef | null>(null);
  const [mode, setMode] = useState<Mode>("day");
  const [index, setIndex] = useState<number | null>(null);

  const days = useMemo<MatchDay[]>(() => {
    const map = new Map<string, MatchDay>();
    for (const m of matches) {
      const k = dayKey(m.matchDate);
      if (!map.has(k)) map.set(k, { key: k, date: m.matchDate, matches: [] });
      map.get(k)!.matches.push(m);
    }
    const arr = [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
    for (const d of arr) d.matches.sort((a, b) => a.matchDate.localeCompare(b.matchDate));
    return arr;
  }, [matches]);

  const weeks = useMemo<Week[]>(() => {
    const map = new Map<string, Week>();
    for (const d of days) {
      const k = weekKey(d.date);
      if (!map.has(k)) map.set(k, { key: k, days: [] });
      map.get(k)!.days.push(d);
    }
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [days]);

  const pageCount = mode === "day" ? days.length : weeks.length;

  const defaultIndex = useMemo(() => {
    if (mode === "day") {
      const i = days.findIndex((d) => d.matches.some((m) => m.tippable));
      return i === -1 ? Math.max(0, days.length - 1) : i;
    }
    const i = weeks.findIndex((w) => w.days.some((d) => d.matches.some((m) => m.tippable)));
    return i === -1 ? Math.max(0, weeks.length - 1) : i;
  }, [mode, days, weeks]);

  if (days.length === 0) return null;

  const current = Math.min(index ?? defaultIndex, Math.max(0, pageCount - 1));
  const changeMode = (m: Mode) => { setMode(m); setIndex(null); };

  let label: string;
  let renderedDays: MatchDay[];
  if (mode === "day") {
    const day = days[current];
    label = formatDayHeading(day.date);
    renderedDays = [day];
  } else {
    const wk = weeks[current];
    label = `${shortDate(wk.days[0].date)} – ${shortDate(wk.days[wk.days.length - 1].date)}`;
    renderedDays = wk.days;
  }
  const matchCount = renderedDays.reduce((s, d) => s + d.matches.length, 0);

  const renderMatches = (list: MatchDto[]) => (
    <div className="grid gap-3 lg:grid-cols-2">
      {list.map((m) => (
        <MatchCard
          key={m.id}
          match={m}
          showStage={showStage}
          onSubmitTip={submitTip}
          onToggleTuti={toggleTuti}
          onOpenTips={setSelectedMatch}
          onOpenTeam={setSelectedTeam}
        />
      ))}
    </div>
  );

  const pager = (
    <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-card/60 px-2 py-2">
      <Button variant="ghost" size="icon" onClick={() => setIndex(Math.max(0, current - 1))} disabled={current === 0} aria-label="Előző">
        <ChevronLeft className="size-5" />
      </Button>
      <div className="min-w-0 text-center">
        <div className="flex items-center justify-center gap-1.5 text-sm font-medium">
          <CalendarDays className="size-4 text-muted-foreground" />
          <span className="truncate">{label}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {current + 1}. / {pageCount} {mode === "day" ? "nap" : "hét"} · {matchCount} meccs
        </div>
      </div>
      <Button variant="ghost" size="icon" onClick={() => setIndex(Math.min(pageCount - 1, current + 1))} disabled={current >= pageCount - 1} aria-label="Következő">
        <ChevronRight className="size-5" />
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Tabs
          items={[{ value: "day", label: "Nap" }, { value: "week", label: "Hét" }]}
          value={mode}
          onValueChange={(v) => changeMode(v as Mode)}
        />
      </div>

      {pager}

      {mode === "day" ? (
        renderMatches(renderedDays[0].matches)
      ) : (
        <div className="space-y-4">
          {renderedDays.map((d) => (
            <div key={d.key}>
              <h3 className="mb-2 text-sm font-semibold text-muted-foreground">{formatDayHeading(d.date)}</h3>
              {renderMatches(d.matches)}
            </div>
          ))}
        </div>
      )}

      {pageCount > 1 && pager}

      <OtherTipsModal match={selectedMatch} onClose={() => setSelectedMatch(null)} />
      <TeamStatsModal team={selectedTeam} onClose={() => setSelectedTeam(null)} />
    </div>
  );
}
